import { useCallback } from 'react'
import { useWaypoints } from './useWaypoints'
import type { WaypointSource } from '@/shared/api/routes'

export interface CreateWaypointData {
  trip_id?: string | null
  route_id?: string | null
  name: string
  latitude: number
  longitude: number
  poi_type?: string | null
  poi_custom_label?: string | null
  notes?: string
  source_type?: string
  order_index?: number
  trip_day?: number // Day number for itinerary organization (defaults to 1)
  source?: WaypointSource | string
  poi_metadata?: Record<string, any>
}

export interface UseWaypointActionsProps {
  tripId: string | null
  routeId: string | null
  fetchWaypoints: (params?: any) => Promise<any[]>
  getWaypointsByTripAndRoute?: (tripId: string, routeId: string) => Promise<any[]>
  getAggregateWaypointsForRoute?: (routeId: string) => Promise<any[]>
  onWaypointDeleted?: (waypointId: string) => void | Promise<void>
  onWaypointCreated?: (waypoint: any) => void
}

export interface UseWaypointActionsReturn {
  // Core waypoint operations
  createWaypoint: (data: CreateWaypointData) => Promise<any>
  updateWaypoint: (waypointId: string, data: Partial<CreateWaypointData>, routeIdParam?: string | null) => Promise<any>
  deleteWaypoint: (waypointId: string, routeId?: string | null) => Promise<void>
  
  // Convenience handlers for different creation scenarios
  handleCreateWaypoint: (waypointData: CreateWaypointData) => Promise<void>
  handleSaveCustomWaypoint: (waypointData: CreateWaypointData) => Promise<void>
  handleSaveSearchResultAsWaypoint: (waypointData: { name: string; address?: string; latitude: number; longitude: number }) => Promise<void>
  handleSaveMapPointAsWaypoint: (coords: { latitude: number; longitude: number }, options?: { poi_type?: 'gas' | 'water' | 'viewpoint' | 'campsite' | 'meetup_spot' | 'trailhead' | 'custom' | null; poi_custom_label?: string | null }) => Promise<void>
  handleSaveWaypointToTrip: (waypoint: any, targetTripId: string) => Promise<void>
}

export function useWaypointActions({
  tripId,
  routeId,
  fetchWaypoints,
  getWaypointsByTripAndRoute,
  getAggregateWaypointsForRoute,
  onWaypointDeleted,
  onWaypointCreated
}: UseWaypointActionsProps): UseWaypointActionsReturn {
  const { createWaypoint: createWaypointApi, updateWaypoint: updateWaypointApi, deleteWaypoint: deleteWaypointApi } = useWaypoints()
  
  // Core waypoint creation
  const createWaypoint = useCallback(async (data: CreateWaypointData) => {
    // Convert CreateWaypointData to CreateWaypointDto format
    // Preserve null values explicitly (don't convert to undefined) so they're included in JSON
    const waypointDto: any = {
      ...data,
      poi_type: data.poi_type as any, // Type assertion for string compatibility
      // Only include trip_id if it's explicitly set (null or string), preserve null for standalone waypoints
      ...(data.trip_id !== undefined && { trip_id: data.trip_id }),
      // Only include route_id if it's explicitly set (null or string), preserve null for standalone waypoints
      ...(data.route_id !== undefined && { route_id: data.route_id })
    }
    const waypoint = await createWaypointApi(waypointDto)
    if (onWaypointCreated) {
      onWaypointCreated(waypoint)
    }
    return waypoint
  }, [createWaypointApi, onWaypointCreated])
  
  // Core waypoint update
  const updateWaypoint = useCallback(async (waypointId: string, data: Partial<CreateWaypointData>, routeIdParam?: string | null) => {
    const routeIdToUse = routeIdParam ?? routeId ?? ''
    const result = await updateWaypointApi(waypointId, data as any, routeIdToUse)
    return result
  }, [updateWaypointApi, routeId])
  
  // Core waypoint deletion
  const deleteWaypoint = useCallback(async (waypointId: string, routeIdParam?: string | null) => {
    try {
      const routeIdToUse = routeIdParam ?? routeId ?? undefined
      await deleteWaypointApi(waypointId, routeIdToUse)

      if (onWaypointDeleted) {
        onWaypointDeleted(waypointId)
      }
      
      // Refresh waypoints after deletion
      // Always refresh all waypoints to keep map view consistent
      // Don't filter by routeId - we want all waypoints visible on map
      if (tripId) {
        await fetchWaypoints({ trip_id: tripId })
      } else {
        await fetchWaypoints({})
      }
    } catch (error) {

      throw error
    }
  }, [deleteWaypointApi, routeId, tripId, fetchWaypoints, getAggregateWaypointsForRoute, onWaypointDeleted])
  
  // Generic waypoint creation handler
  const handleCreateWaypoint = useCallback(async (waypointData: CreateWaypointData) => {
    if (!routeId && !waypointData.route_id && !waypointData.trip_id) {

      return
    }

    try {
      await createWaypoint({
        ...waypointData,
        trip_id: waypointData.trip_id ?? tripId ?? null,
        route_id: waypointData.route_id ?? routeId ?? null
      })

      // Refresh waypoints
      if (tripId) {
        await fetchWaypoints({ trip_id: tripId })
      } else {
        await fetchWaypoints({})
      }
    } catch (error) {

      throw error
    }
  }, [createWaypoint, routeId, tripId, fetchWaypoints])
  
  // Save custom waypoint (from dialog)
  const handleSaveCustomWaypoint = useCallback(async (waypointData: CreateWaypointData) => {
    try {
      await createWaypoint({
        ...waypointData,
        trip_id: waypointData.trip_id ?? tripId ?? null,
        route_id: waypointData.route_id ?? routeId ?? null
      })

      // Refresh waypoints list
      if (tripId) {
        await fetchWaypoints({ trip_id: tripId })
      } else {
        await fetchWaypoints({})
      }
    } catch (error) {

      throw error
    }
  }, [createWaypoint, tripId, fetchWaypoints])
  
  // Save search result as waypoint
  const handleSaveSearchResultAsWaypoint = useCallback(async (waypointData: { name: string; address?: string; latitude: number; longitude: number }) => {
    try {
      await createWaypoint({
        trip_id: tripId || null,
        route_id: routeId || null,
        name: waypointData.name,
        latitude: waypointData.latitude,
        longitude: waypointData.longitude,
        poi_type: null, // Default to null (no label) - user can choose their label
        trip_day: 1, // Default to day 1 when adding from search (same as map click)
        notes: waypointData.address ? `Address: ${waypointData.address}` : undefined
      })
      
      // onWaypointCreated callback (from useItineraryBuilder) will handle refreshing waypoints
      // No need to refetch here - the optimistic update ensures immediate UI feedback
      // The background refresh in onWaypointCreated ensures consistency

    } catch (error) {

      throw error
    }
  }, [createWaypoint, tripId, routeId])
  
  // Save clicked map point as waypoint
  // Trip-first: tripId is required, route_id defaults to null (trip-level waypoint)
  const handleSaveMapPointAsWaypoint = useCallback(async (
    coords: { latitude: number; longitude: number },
    options?: { poi_type?: 'gas' | 'water' | 'viewpoint' | 'campsite' | 'meetup_spot' | 'trailhead' | 'custom' | null; poi_custom_label?: string | null }
  ) => {
    if (!tripId) {

      throw new Error('Trip ID is required')
    }

    try {
      // Generate a smart name based on POI type
      const { generateWaypointName } = await import('@/modules/trips/utils/waypointNameGenerator')
      const waypointName = generateWaypointName(
        options?.poi_type ?? null,
        options?.poi_custom_label ?? null
      )

      // Waypoint-first: tripId required, route_id defaults to null (trip-level waypoint)
      // Default trip_day to 1 when adding from map click
      const newWaypoint = await createWaypoint({
        trip_id: tripId, // Required for waypoint-first workflow
        route_id: routeId || null, // Default to null for trip-level waypoints
        name: waypointName,
        latitude: coords.latitude,
        longitude: coords.longitude,
        poi_type: options?.poi_type ?? null, // Use provided type or default to null (Route Stop)
        poi_custom_label: options?.poi_custom_label ?? null,
        trip_day: 1, // Default to day 1 when adding from map click
        notes: `Coordinates: ${coords.latitude.toFixed(6)}, ${coords.longitude.toFixed(6)}`
      })

      // onWaypointCreated callback (from useItineraryBuilder) will handle refreshing waypoints
      // No need to refetch here - the waypoint from creation response is sufficient
    } catch (error) {

      throw error
    }
  }, [createWaypoint, tripId, routeId, fetchWaypoints])
  
  // Save waypoint to trip (from existing waypoint)
  const handleSaveWaypointToTrip = useCallback(async (waypoint: any, targetTripId: string) => {
    try {
      await createWaypoint({
        trip_id: targetTripId,
        route_id: null,
        name: Array.isArray(waypoint.name) ? waypoint.name[0] : waypoint.name || 'Waypoint',
        latitude: waypoint.latitude,
        longitude: waypoint.longitude,
        poi_type: waypoint.poi_type || undefined,
        source_type: 'gpx_import'
      })

      // If user is currently viewing this trip, refresh trip waypoints
      if (targetTripId === tripId && routeId && getWaypointsByTripAndRoute) {
        await getWaypointsByTripAndRoute(tripId, routeId)
      } else if (targetTripId === tripId) {
        await fetchWaypoints({ trip_id: tripId })
      }
    } catch (error) {

      throw error
    }
  }, [createWaypoint, tripId, routeId, getWaypointsByTripAndRoute, fetchWaypoints])
  
  return {
    // Core operations
    createWaypoint,
    updateWaypoint,
    deleteWaypoint,
    
    // Convenience handlers
    handleCreateWaypoint,
    handleSaveCustomWaypoint,
    handleSaveSearchResultAsWaypoint,
    handleSaveMapPointAsWaypoint,
    handleSaveWaypointToTrip
  }
}

